import { useState, useEffect } from "react";
import "./UploadEventImageModal.css";

const UploadEventImageModal = ({
  visible,
  onClose,
  onSubmit,
  maxImages = 4,
  title = "Upload Event Image Attachment",
  subtitle = "Select and upload a clear image of the event card, invite card or banner on each card.",
  defaultImages,
  preferredCurrency,
}) => {
  const [images, setImages] = useState([]);

  useEffect(() => {
    if (visible && defaultImages?.length) {
      setImages(defaultImages);
    }
  }, [visible, defaultImages]);

  const openFileSelector = () => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/*";
    fileInput.multiple = true;
    fileInput.onchange = handleFileSelection;
    fileInput.click();
  };

  const handleFileSelection = async (event) => {
    const selectedFiles = Array.from(event.target.files);

    if (images.length + selectedFiles.length > maxImages) {
      alert(`You can only upload up to ${maxImages} images.`);
      return;
    }

    const newImages = await Promise.all(
      selectedFiles.map(async (file) => {
        if (!(file instanceof File)) {
          // console.error("Invalid file object:", file);
          return null;
        }

        const previewUrl = URL.createObjectURL(file);
        return {
          uri: previewUrl,
          type: file.type,
          name: file.name,
          file: file,
        };
      })
    );

    const validImages = newImages.filter(Boolean);
    // console.log(`Selected ${validImages.length} valid images`);
    setImages((prevImages) => [...prevImages, ...validImages]);
  };

  const removeImage = (index) => {
    setImages((prevImages) => {
      const updatedImages = [...prevImages];
      if (updatedImages[index].uri.startsWith("blob:")) {
        URL.revokeObjectURL(updatedImages[index].uri);
      }
      updatedImages.splice(index, 1);
      return updatedImages;
    });
  };

  const handleSubmit = () => {
    if (images.length > 0) {
      onSubmit(images);
      onClose();
    } else {
      alert("Please upload at least one image.");
    }
  };

  if (!visible) return null;

  return (
    <div className="uei-modal-overlay" onClick={onClose}>
      <div className="uei-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="uei-header">
          <div className="uei-header-bar"></div>
          <button onClick={onClose} className="uei-close-button">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="uei-content">
          <h2 className="uei-title">{title}</h2>
          <p className="uei-subtitle">{subtitle}</p>

          <div className="uei-image-grid">
            {Array(maxImages)
              .fill(0)
              .map((_, index) => {
                if (index < images.length) {
                  return (
                    <div key={index} className="uei-image-placeholder">
                      <img
                        src={images[index].uri || "/placeholder.svg"}
                        alt={`Preview ${index}`}
                        className="uei-thumbnail-image"
                      />
                      <button
                        className="uei-remove-image-button"
                        onClick={() => removeImage(index)}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </div>
                  );
                } else {
                  // Render empty placeholder
                  return (
                    <div
                      key={index}
                      className="uei-image-placeholder uei-add-placeholder"
                      onClick={openFileSelector}
                    >
                      <div className="uei-pic-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect
                            x="3"
                            y="3"
                            width="18"
                            height="18"
                            rx="2"
                            ry="2"
                          ></rect>
                          <circle cx="8.5" cy="8.5" r="1.5"></circle>
                          <polyline points="21 15 16 10 5 21"></polyline>
                          <line x1="14" y1="14" x2="14" y2="14"></line>
                          <line x1="17" y1="8" x2="17" y2="8"></line>
                          <line x1="13" y1="8" x2="17" y2="8"></line>
                          <line x1="15" y1="6" x2="15" y2="10"></line>
                        </svg>
                      </div>
                    </div>
                  );
                }
              })}
          </div>
        </div>

        <div className="uei-button-container">
          <button className="uei-back-button" onClick={onClose}>
            Back
          </button>
          <button
            className={`uei-add-button ${
              images.length === 0 ? "uei-disabled" : ""
            }`}
            onClick={handleSubmit}
            disabled={images.length === 0}
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadEventImageModal;
